/**
 * useNotificationsConnection — the single app-shell mount point that drives
 * the notifications connection manager from React auth state (GW-8887, FR-1).
 *
 * Called exactly once, in `App.tsx`, beside `useAuthErrorHandler()`. It owns
 * the *policy* (when to open/close the socket); `connectionManager.ts` owns the
 * *mechanism* (the single connection, backoff, cleanup). Splitting them this
 * way keeps "exactly one connection app-wide" a property of the module
 * singleton, independent of how many times this hook happens to render.
 *
 * Connect precondition — ALL must hold (GW-11124: notifications is
 * foundational and must never be feature-flag gated, so the connection no
 * longer reads any flag):
 *   - the user is authenticated AND has a `userId` (the Cognito `sub` the
 *     client will derive its channel from).
 *
 * Disconnect happens on:
 *   - loss of authentication (sign-out, or `useAuthErrorHandler`'s reaction to
 *     a 401 — both flip `isAuthenticated` to false in the store);
 *   - an explicit `session-expired` auth event (belt-and-suspenders: tear the
 *     socket down immediately rather than waiting for the store re-render);
 *   - unmount (HMR/StrictMode).
 *
 * The connection manager's `start`/`stop` are themselves idempotent and
 * generation-guarded, so StrictMode's deliberate double-invoke of effects is
 * safe: two starts collapse to one connection, and the paired cleanups leave
 * the manager stopped-then-restarted without leaking a socket.
 */
import { useEffect } from 'react';

import { authEvents } from '@/lib/authEvents';
import {
  startNotificationsConnection,
  stopNotificationsConnection,
} from '@/lib/notifications/connectionManager';
import { useAuthStore } from '@/stores/store';

export function useNotificationsConnection(): void {
  const { isAuthenticated, user } = useAuthStore();
  const userId = user?.userId;
  const shouldConnect = Boolean(isAuthenticated && userId);

  useEffect(() => {
    if (!shouldConnect) {
      return;
    }

    void startNotificationsConnection();

    // Explicit session-expired signal: tear down immediately (the store
    // re-render that also flips shouldConnect is a separate, redundant path).
    const onSessionExpired = (): void => {
      void stopNotificationsConnection();
    };
    authEvents.on('session-expired', onSessionExpired);

    return () => {
      authEvents.off('session-expired', onSessionExpired);
      void stopNotificationsConnection();
    };
  }, [shouldConnect]);
}
